import {Link as BaseRouterLink} from 'react-router';

import styled, {css} from 'styled-components';

import {ESize, TSize, mixins} from '@edna/components';
import {TSpacingSize} from '@edna/components/types';

import {link} from 'src/styles/mixins';

type TIndent = {
  topIndent?: TSpacingSize;
  rightIndent?: TSpacingSize;
  bottomIndent?: TSpacingSize;
  leftIndent?: TSpacingSize;
};

type TTextProps = TIndent & {
  isInline?: boolean;
  isCenter?: boolean;
  color?: TThemeColor;
  size?: TSize | 'XXS';
  secondary?: boolean;
  weight?: number;
};

const getFontSize = (size?: TSize | 'XXS') => {
  switch (size) {
    case 'XXS':
      return mixins.f1;
    case ESize.XS:
      return mixins.f2;
    case ESize.S:
      return mixins.f3;
    case ESize.L:
      return mixins.f5;
    case ESize.XL:
      return mixins.f6;
    case ESize.M:
    default:
      return mixins.f4;
  }
};

const getSpacing =
  (indent: TSpacingSize) =>
  ({theme}: TTheme) =>
    theme.spacing[`s${indent}`];

const Text = styled.div<TTextProps>`
  ${({
    theme,
    color = 'grey900',
    secondary,
    weight = 400,
    topIndent = '0',
    rightIndent = '0',
    bottomIndent = '0',
    leftIndent = '0',
    isInline,
    isCenter,
    size,
  }) => css`
    ${getFontSize(size)};
    text-align: ${isCenter ? 'center' : 'inherit'};
    margin-top: ${getSpacing(topIndent)};
    margin-right: ${getSpacing(rightIndent)};
    margin-bottom: ${getSpacing(bottomIndent)};
    margin-left: ${getSpacing(leftIndent)};
    color: ${secondary ? theme.branding.content.secondary : theme.palette[color]};
    font-weight: ${weight};
    display: ${isInline ? 'inline' : 'block'};
    overflow-wrap: anywhere;
  `}
`;

const SecondaryText = styled(Text)`
  color: ${({theme}) => theme.branding.content.secondary};
`;

const Hint = styled(SecondaryText)`
  ${mixins.f2}
  ${({theme, topIndent}) => css`
    margin-top: ${topIndent === '0' ? 0 : theme.spacing[`s${topIndent ?? 2}`]};
  `}
`;

const BigText = styled(Text)`
  ${mixins.f5}
  font-weight: 500;
  margin-bottom: ${({bottomIndent = '4'}: TTextProps) => getSpacing(bottomIndent)};
`;

const RouterLink = styled(BaseRouterLink)`
  ${link}
`;

const Link = styled.a.attrs({
  target: '_blank',
  rel: 'noopener noreferrer',
})`
  ${link}
`;

const InlineLink = styled(Link)<{weight?: number}>`
  font-weight: ${({weight}) => weight ?? 400};
  text-decoration: underline;
  color: currentColor;
`;

const InlineRouterLink = styled(BaseRouterLink)<{weight?: number}>`
  font-weight: ${({weight}) => weight ?? 400};
  text-decoration: underline;
  color: currentColor;
`;

const SecondaryLink = styled(Link)`
  text-decoration: underline;
  ${({theme: {branding}}) => css`
    color: ${branding.content.secondary};
    &:active {
      color: ${branding.content.secondary};
    }
  `}
`;

const FormTitle = styled.div<{noMargin?: boolean}>`
  ${mixins.f5}

  ${({theme, noMargin}) => css`
    color: ${theme.branding.content.secondary};
    ${!noMargin &&
    css`
      margin-bottom: ${theme.spacing.s4};
    `}
  `};
`;

const Error = styled(Text)`
  ${mixins.f2}
  color: ${({theme}) => theme.palette.red500};
`;

const NameLink = styled.div`
  ${link}
  ${mixins.truncate}
`;

export {
  Text,
  SecondaryText,
  Hint,
  BigText,
  InlineLink,
  InlineRouterLink,
  SecondaryLink,
  Link,
  RouterLink,
  FormTitle,
  getFontSize,
  Error,
  NameLink,
};
